import React, { useEffect, useMemo, useState } from "react";
import { UsageUserDrawer } from "@/components/UsageUserDrawer";
import { UsageRangePicker, usageDefaultRange } from "@/components/UsageRangePicker";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip } from "recharts";
import { BarChart3, RefreshCw, TrendingUp, TrendingDown, LayoutGrid, ChevronDown, ChevronUp } from "lucide-react";

const parse = (s) => new Date(`${s}T00:00:00`);
const fmtDay = (s) => parse(s).toLocaleDateString("en-GB", { day: "numeric", month: "short" });
const fmtMins = (m) => (m >= 60 ? `${Math.floor(m / 60)}h ${m % 60}m` : `${m}m`);
const ago = (iso) => {
    const s = (Date.now() - new Date(iso).getTime()) / 1000;
    if (s < 3600) return `${Math.max(1, Math.round(s / 60))}m ago`;
    if (s < 86400) return `${Math.round(s / 3600)}h ago`;
    return `${Math.round(s / 86400)}d ago`;
};
const Panel = ({ title, right, children, className = "", testid }) => (
    <div className={`rounded-xl border border-white/10 bg-[#161616] p-4 ${className}`} data-testid={testid}>
        {title && (
            <div className="flex items-center justify-between mb-4">
                <div className="text-[10px] uppercase tracking-[0.14em] text-white/60 font-semibold">{title}</div>
                {right}
            </div>
        )}
        {children}
    </div>
);

const Change = ({ v }) => {
    if (v == null) return <span className="text-[10px] text-white/30">—</span>;
    const up = v >= 0;
    const Icon = up ? TrendingUp : TrendingDown;
    return (
        <span className={`inline-flex items-center gap-0.5 px-1.5 h-4 rounded text-[10px] font-semibold ${up ? "bg-emerald-500/15 text-emerald-400" : "bg-rose-500/15 text-rose-400"}`}>
            <Icon className="h-2.5 w-2.5" />{up ? "+" : ""}{v}%
        </span>
    );
};

const Kpis = ({ kpis }) => {
    const items = [
        ["active_users", "Active users", (v) => v],
        ["page_views", "Page views", (v) => v],
        ["sessions", "Sessions", (v) => v],
        ["active_minutes", "Active time", fmtMins],
    ];
    return (
        <div className="grid grid-cols-2 lg:grid-cols-4 border-b border-white/10">
            {items.map(([k, label, f], i) => (
                <div key={k} className={`px-4 py-3 ${i ? "lg:border-l border-white/10" : "bg-white/[0.04]"}`} data-testid={`usage-kpi-${k}`}>
                    <div className="text-[10px] uppercase tracking-[0.14em] text-white/60 font-semibold">{label}</div>
                    <div className="mt-1 flex items-baseline gap-2">
                        <span className="text-2xl font-bold text-white tabular-nums">{f(kpis[k].value)}</span>
                        <Change v={kpis[k].change} />
                    </div>
                </div>
            ))}
        </div>
    );
};

const TrendChart = ({ series }) => (
    <div className="px-2 pt-3 pb-2 h-64" data-testid="usage-chart">
        <div className="flex justify-end gap-4 pr-4 text-[10px] text-white/50">
            <span className="inline-flex items-center gap-1.5"><span className="h-px w-4 bg-[#3B82F6]" />this period</span>
            <span className="inline-flex items-center gap-1.5"><span className="h-px w-4 bg-white/40" />last period</span>
        </div>
        <ResponsiveContainer width="100%" height="90%">
            <LineChart data={series} margin={{ top: 8, right: 16, left: -16, bottom: 0 }}>
                <CartesianGrid stroke="rgba(255,255,255,0.05)" vertical={false} />
                <XAxis dataKey="date" tickFormatter={fmtDay} tick={{ fill: "rgba(255,255,255,0.45)", fontSize: 10 }} axisLine={false} tickLine={false} />
                <YAxis allowDecimals={false} tick={{ fill: "rgba(255,255,255,0.45)", fontSize: 10 }} axisLine={false} tickLine={false} />
                <Tooltip
                    contentStyle={{ background: "#0d0d0d", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 8, fontSize: 12, color: "#fff" }}
                    labelFormatter={fmtDay}
                    formatter={(v, n) => [v, n === "this" ? "This period" : "Last period"]}
                />
                <Line type="monotone" dataKey="last" stroke="rgba(255,255,255,0.4)" strokeWidth={1.5} dot={false} />
                <Line type="monotone" dataKey="this" stroke="#3B82F6" strokeWidth={2} dot={false} />
            </LineChart>
        </ResponsiveContainer>
    </div>
);

const ShowMore = ({ open, setOpen, hidden, testid }) =>
    hidden > 0 || open ? (
        <button onClick={() => setOpen(!open)} data-testid={testid} className="mt-2 w-full inline-flex items-center justify-center gap-1 text-[10px] uppercase tracking-[0.14em] text-white/50 hover:text-white font-semibold">
            {open ? "Show less" : "Show more"} {open ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
        </button>
    ) : null;

const Bar = ({ pct }) => (
    <div className="mt-1.5 h-1.5 rounded-full bg-white/10 overflow-hidden">
        <div className="h-full rounded-full bg-[#5EEAD4] transition-[width] duration-500" style={{ width: `${Math.max(2, pct)}%` }} />
    </div>
);

const TopModules = ({ modules }) => {
    const [mode, setMode] = useState("by_views");
    const [open, setOpen] = useState(false);
    const list = modules[mode];
    const shown = open ? list : list.slice(0, 5);
    return (
        <Panel
            title="Top modules"
            testid="usage-top-modules"
            right={
                <div className="inline-flex rounded-md bg-white/5 p-0.5 text-[10px] font-semibold">
                    {[["by_views", "By page views"], ["by_time", "By time"]].map(([k, l]) => (
                        <button key={k} onClick={() => setMode(k)} data-testid={`usage-modules-${k}`} className={`px-2 h-5 rounded ${mode === k ? "bg-white/15 text-white" : "text-white/50 hover:text-white"}`}>{l}</button>
                    ))}
                </div>
            }
        >
            {list.length === 0 ? <div className="text-xs text-white/40 py-6 text-center">No activity in this period yet.</div> : (
                <div className="space-y-3">
                    {shown.map((m) => (
                        <div key={m.module}>
                            <div className="flex items-center justify-between text-sm">
                                <span className="inline-flex items-center gap-2 text-white font-medium"><LayoutGrid className="h-3.5 w-3.5 text-white/40" />{m.module}</span>
                                <span className="text-xs text-white/60 tabular-nums">{m.pct}%</span>
                            </div>
                            <Bar pct={m.pct} />
                        </div>
                    ))}
                </div>
            )}
            <ShowMore open={open} setOpen={setOpen} hidden={list.length - 5} testid="usage-modules-more" />
        </Panel>
    );
};

const TopUsers = ({ users, onPick }) => {
    const [open, setOpen] = useState(false);
    const max = Math.max(1, ...users.map((u) => u.active_minutes));
    const shown = open ? users : users.slice(0, 5);
    return (
        <Panel title="Top users" right={<span className="text-[10px] text-white/40">By active time · click for details</span>} testid="usage-top-users">
            {users.length === 0 ? <div className="text-xs text-white/40 py-6 text-center">No activity in this period yet.</div> : (
                <div className="space-y-3">
                    {shown.map((u) => (
                        <button key={u.user_id} onClick={() => onPick(u.user_id)} className="w-full text-left group" data-testid={`usage-user-${u.user_id}`}>
                            <div className="flex items-center justify-between gap-2 text-sm">
                                <span className="flex items-center gap-2 min-w-0">
                                    {u.picture ? <img src={u.picture} alt="" className="h-4 w-4 rounded-full object-cover" /> : <span className="h-4 w-4 rounded-full bg-[#2EB4AC] text-[8px] font-bold text-white flex items-center justify-center">{(u.name || "?")[0]}</span>}
                                    <span className="text-white font-semibold group-hover:underline truncate">{u.name}</span>
                                    <span className="text-[11px] text-white/40 truncate">{u.days_active}d active · {u.last_module} · {ago(u.last_seen)}</span>
                                </span>
                                <span className="text-xs text-white/70 tabular-nums shrink-0">{fmtMins(u.active_minutes)}</span>
                            </div>
                            <Bar pct={(u.active_minutes * 100) / max} />
                        </button>
                    ))}
                </div>
            )}
            <ShowMore open={open} setOpen={setOpen} hidden={users.length - 5} testid="usage-users-more" />
        </Panel>
    );
};

const Engagement = ({ e }) => (
    <Panel title="Engagement" right={<span className="text-[10px] text-white/40">Relative to end date</span>} testid="usage-engagement">
        <div className="grid grid-cols-3 gap-2">
            {[["DAU", e.dau, "Today"], ["WAU", e.wau, "Last 7 days"], ["MAU", e.mau, "Last 30 days"]].map(([l, v, s]) => (
                <div key={l} className="rounded-lg bg-white/[0.04] p-3" data-testid={`usage-${l.toLowerCase()}`}>
                    <div className="text-[10px] text-white/50 font-semibold">{l}</div>
                    <div className="text-xl font-bold text-white tabular-nums">{v}</div>
                    <div className="text-[10px] text-white/40">{s}</div>
                </div>
            ))}
        </div>
        <div className="mt-4 flex items-center justify-between text-xs text-white">
            <span>Stickiness (DAU / MAU)</span><span className="tabular-nums">{e.stickiness}%</span>
        </div>
        <Bar pct={e.stickiness} />
        <div className="mt-4 grid grid-cols-2 gap-2 text-[11px]">
            <div className="flex justify-between rounded-md bg-white/[0.04] px-3 py-2"><span className="text-white/50">Logins</span><span className="text-white font-semibold tabular-nums">{e.logins}</span></div>
            <div className="flex justify-between rounded-md bg-white/[0.04] px-3 py-2"><span className="text-white/50">Avg session</span><span className="text-white font-semibold tabular-nums">{fmtMins(Math.round(e.avg_session_min))}</span></div>
        </div>
    </Panel>
);

const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const PeakHours = ({ heat }) => {
    const max = Math.max(1, ...heat.flat());
    return (
        <Panel title="Peak hours" right={<span className="text-[10px] text-white/40">IST · active time</span>} testid="usage-peak-hours">
            <div className="overflow-x-auto">
                <div className="min-w-[440px]">
                    <div className="grid grid-cols-[28px_repeat(24,minmax(0,1fr))] gap-[3px] text-[8px] text-white/40 mb-1">
                        <span />
                        {Array.from({ length: 24 }, (_, h) => <span key={h} className="text-center">{h % 3 === 0 ? `${h % 12 || 12}${h < 12 ? "a" : "p"}` : ""}</span>)}
                    </div>
                    {heat.map((row, d) => (
                        <div key={d} className="grid grid-cols-[28px_repeat(24,minmax(0,1fr))] gap-[3px] mb-[3px]">
                            <span className="text-[9px] text-white/50 leading-4">{DAYS[d]}</span>
                            {row.map((v, h) => (
                                <span
                                    key={h}
                                    title={`${DAYS[d]} ${h}:00 · ${fmtMins(v)}`}
                                    className="h-4 rounded-[3px]"
                                    style={{ background: v ? `rgba(94,234,212,${0.15 + 0.85 * (v / max)})` : "rgba(255,255,255,0.05)" }}
                                />
                            ))}
                        </div>
                    ))}
                </div>
            </div>
        </Panel>
    );
};

export const UsageAnalytics = () => {
    const [range, setRange] = useState(usageDefaultRange);
    const [picked, setPicked] = useState(null);
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);

    const load = async () => {
        setLoading(true);
        try {
            const r = await api.get("/usage/analytics", { params: range });
            setData(r.data);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Couldn't load usage analytics");
        } finally {
            setLoading(false);
        }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => { load(); }, [range.start, range.end]);

    const scopeNote = useMemo(() => ({ all: "Everyone in the organisation", team: "You and the people in your reporting line", self: "Only your own usage" }[data?.scope] || ""), [data]);

    return (
        <div className="rounded-2xl bg-[#0f0f0f] border border-black p-5 sm:p-6 text-white shadow-xl" data-testid="usage-analytics">
            <h2 className="font-heading text-xl font-bold">Analytics</h2>
            <p className="text-xs text-white/50 mt-0.5">Track active users, page views, module adoption and more.{scopeNote && ` · ${scopeNote}`}</p>

            <div className="mt-5 flex items-center justify-between gap-3">
                <div className="inline-flex items-center gap-2 text-sm font-semibold"><BarChart3 className="h-4 w-4 text-[#3B82F6]" /> App visits</div>
                <div className="flex items-center gap-2">
                    <UsageRangePicker range={range} setRange={setRange} />
                    <button onClick={load} data-testid="usage-refresh" className="h-8 w-8 inline-flex items-center justify-center rounded-md border border-white/15 bg-white/5 hover:bg-white/10" aria-label="Refresh">
                        <RefreshCw className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} />
                    </button>
                </div>
            </div>

            {!data ? (
                <div className="mt-4 h-80 rounded-xl bg-white/5 animate-pulse" />
            ) : (
                <>
                    <div className="mt-3 rounded-xl border border-white/10 bg-[#161616] overflow-hidden">
                        <Kpis kpis={data.kpis} />
                        <TrendChart series={data.series} />
                    </div>
                    <div className="mt-4 grid grid-cols-1 lg:grid-cols-2 gap-4">
                        <TopModules modules={data.modules} />
                        <TopUsers users={data.top_users} onPick={setPicked} />
                        <Engagement e={data.engagement} />
                        <PeakHours heat={data.peak_hours} />
                    </div>
                    <div className="mt-4 text-[10px] text-white/35">
                        Compared with {data.range.prev_start} → {data.range.prev_end}. Tracking started when this feature went live; raw events are kept for 180 days.
                    </div>
                </>
            )}
            <UsageUserDrawer userId={picked} range={range} onClose={() => setPicked(null)} />
        </div>
    );
};
